import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { combineLatest, firstValueFrom, from, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { Timestamp } from '@angular/fire/firestore';
import { AccountInvite } from '../../../models/account-invite.model';
import { AccountInviteService } from '../../../services/account-invite.service';
import { AuthService } from '../../../services/auth.service';
import { AccountContextService } from '../../../services/account-context.service';

@Component({
  selector: 'app-accept-invite',
  templateUrl: './accept-invite.component.html',
  styleUrls: ['./accept-invite.component.css']
})
export class AcceptInviteComponent implements OnInit {
  invite: AccountInvite | null = null;
  status: 'loading' | 'ready' | 'accepted' | 'rejected' | 'expired' | 'error' = 'loading';
  errorMessage: string | null = null;
  accountId = '';
  inviteId = '';
  currentUser: { uid: string; email?: string | null } | null = null;
  redirectTo = '';

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly accountInviteService: AccountInviteService,
    private readonly authService: AuthService,
    private readonly accountContextService: AccountContextService
  ) {}

  ngOnInit(): void {
    const routeParamMap$ = typeof (this.route.paramMap as { pipe?: unknown }).pipe === 'function'
      ? this.route.paramMap
      : of(this.route.snapshot?.paramMap ?? this.route.paramMap);

    combineLatest([
      routeParamMap$,
      this.authService.user$
    ])
      .pipe(
        map(([params, user]) => ({
          accountId: params.get('accountId') ?? '',
          inviteId: params.get('inviteId') ?? '',
          user
        })),
        switchMap(({ accountId, inviteId, user }) => {
          this.accountId = accountId;
          this.inviteId = inviteId;
          this.currentUser = user ? { uid: user.uid, email: user.email ?? null } : null;
          this.redirectTo = this.router.url;
          return from(this.accountInviteService.getInvite(accountId, inviteId));
        })
      )
      .subscribe({
        next: invite => {
          this.invite = invite;
          if (!invite) {
            this.status = 'error';
            this.errorMessage = 'Invite not found.';
          } else if (invite.status === 'pending' && this.isInviteExpired(invite)) {
            this.status = 'expired';
            this.errorMessage = 'Invite has expired.';
          } else if (invite.status === 'pending') {
            this.status = 'ready';
          } else if (invite.status === 'accepted') {
            this.status = 'accepted';
          } else if (invite.status === 'rejected') {
            this.status = 'rejected';
          } else if (invite.status === 'expired') {
            this.status = 'expired';
          } else {
            this.status = 'error';
            this.errorMessage = `Invite is already ${invite.status}.`;
          }
        },
        error: (error: any) => {
          this.status = 'error';
          this.errorMessage = error?.message || 'Unable to load invite.';
        }
      });
  }

  async acceptInvite(): Promise<void> {
    if (!this.accountId || !this.inviteId) {
      return;
    }

    const user = await firstValueFrom(this.authService.user$);
    if (!user?.uid) {
      this.status = 'error';
      this.errorMessage = 'Please sign in to accept the invite.';
      return;
    }

    this.status = 'loading';
    this.errorMessage = null;

    try {
      await this.accountInviteService.acceptInvite(this.accountId, this.inviteId, {
        uid: user.uid,
        email: user.email ?? null
      });
      this.accountContextService.setActiveAccount(this.accountId);
      this.status = 'accepted';
    } catch (error: any) {
      this.status = 'error';
      this.errorMessage = error?.message || 'Unable to accept invite.';
    }
  }

  async rejectInvite(): Promise<void> {
    if (!this.accountId || !this.inviteId) {
      return;
    }

    const user = await firstValueFrom(this.authService.user$);
    if (!user?.uid) {
      this.status = 'error';
      this.errorMessage = 'Please sign in to reject the invite.';
      return;
    }

    this.status = 'loading';
    this.errorMessage = null;

    try {
      await this.accountInviteService.rejectInvite(this.accountId, this.inviteId, {
        uid: user.uid,
        email: user.email ?? null
      });
      this.status = 'rejected';
    } catch (error: any) {
      this.status = 'error';
      this.errorMessage = error?.message || 'Unable to reject invite.';
    }
  }

  formatTimestamp(value: any): Date | null {
    if (!value) {
      return null;
    }

    const date = value?.toDate ? value.toDate() : new Date(value);
    return isNaN(date.getTime()) ? null : date;
  }

  private isInviteExpired(invite: AccountInvite): boolean {
    if (!invite.expiresAt) {
      return false;
    }

    const expirationDate = invite.expiresAt instanceof Timestamp
      ? invite.expiresAt.toDate()
      : invite.expiresAt?.toDate
        ? invite.expiresAt.toDate()
        : new Date(invite.expiresAt);

    if (Number.isNaN(expirationDate.getTime())) {
      return false;
    }

    return expirationDate.getTime() <= Date.now();
  }
}
